import * as Sentry from '@sentry/capacitor';
import * as SentryReact from '@sentry/react';
import { Capacitor } from '@capacitor/core';
import {
  isInjectedScriptError,
  isAnalyticsOfflineError,
} from '@/lib/sentry-noise';
import { hasBearerPath } from '@/lib/bearer-paths';
import { redactPrivatePaths } from '@/lib/private-paths';
import {
  IMAGE_SPANS,
  REPLAY_BLOCK,
  REPLAY_MASK,
  REPLAY_MASK_ATTRIBUTES,
  SELECTOR_SPAN_OPS,
  imageHost,
  maskReplayText,
  scrubRecordingEvent,
  scrubReplayEvent,
  scrubSelector,
  scrubSpanData,
  scrubUrl,
} from '@/lib/sentry-privacy';

// An App Link can open the emailed reset link inside the app, where Inertia
// fetches it by XHR: the token would land in breadcrumbs, which the native SDK
// copies onto native crash reports too. Redact before anything is stored.
// Queries go too: search pages and lookups put typed text there, and so do
// friends' handles in profile paths. The replay copies navigation and network
// breadcrumbs after this runs. A click or key breadcrumb's message describes
// the element, whose alt or aria-label can name another person (#520).
export function scrubBreadcrumb(crumb) {
  if (!crumb) return crumb;
  if (String(crumb.category ?? '').startsWith('ui.')) {
    crumb.message = scrubSelector(crumb.message);
  }
  if (crumb.message) crumb.message = redactPrivatePaths(crumb.message);
  if (crumb.data) {
    for (const key of ['url', 'from', 'to']) {
      if (crumb.data[key]) crumb.data[key] = scrubUrl(crumb.data[key]);
    }
  }
  return crumb;
}

// Span attributes that hold a request's address, and the parts of it the SDK
// copies out separately.
const SPAN_URL_KEYS = ['url', 'http.url'];
const SPAN_URL_PARTS = ['http.query', 'http.fragment'];
const ADDRESS = /^(?:https?:\/\/|\/)/;

// Span names embed a URL ("GET https://…", a page-load's document URL, a
// resource path) or describe an element: an interaction's target, a web
// vital's element. Element descriptions lose their attribute values (#520).
function scrubSpan(span) {
  if (typeof span.description === 'string') {
    if (SELECTOR_SPAN_OPS.test(span.op)) {
      span.description = scrubSelector(span.description);
    } else if (IMAGE_SPANS.has(span.op)) {
      span.description = imageHost(span.description);
    } else {
      span.description = span.description
        .split(' ')
        .map((part) => (ADDRESS.test(part) ? scrubUrl(part) : part))
        .join(' ');
    }
  }
  const data = span.data;
  if (!data) return;
  scrubSpanData(data);
  for (const key of SPAN_URL_KEYS) {
    if (typeof data[key] === 'string') data[key] = scrubUrl(data[key]);
  }
  for (const key of SPAN_URL_PARTS) delete data[key];
}

// URLs inside free text, such as a failed Inertia visit's message, "Network
// error (https://bosko.app/app/bible/search?q=…)".
const ABSOLUTE_URL = /https?:\/\/[^\s)'"]+/g;
const PATH_TOKEN = /(^|[\s(])(\/[^\s)'"]+)/g;

function scrubUrlsIn(text) {
  return text
    .replace(ABSOLUTE_URL, (url) => scrubUrl(url))
    .replace(PATH_TOKEN, (match, lead, path) => lead + scrubUrl(path));
}

// Standalone spans (INP web vitals) skip beforeSend and beforeSendTransaction.
// Their `transaction` attribute is the raw pathname, which can hold a bearer
// token or a friend's handle, and their name describes the element
// (scrubSpan). beforeSendSpan also sees each transaction's root and child
// spans.
export function scrubStandaloneSpan(span) {
  if (typeof span?.data?.transaction === 'string') {
    span.data.transaction = scrubUrl(span.data.transaction);
  }
  if (span) scrubSpan(span);
  return span;
}

// Error and transaction events. A transaction keeps raw URLs in its span
// names and attributes, and any event carries the previous page as Referer;
// search pages put typed text in those queries (#518). A page load also
// describes its LCP element and layout-shift sources, labels included (#520).
export function scrubEvent(event) {
  if (event.request?.url) event.request.url = scrubUrl(event.request.url);
  const headers = event.request?.headers;
  if (headers?.Referer) headers.Referer = scrubUrl(headers.Referer);
  if (event.transaction)
    event.transaction = redactPrivatePaths(event.transaction);
  const trace = event.contexts?.trace?.data;
  if (trace?.['lcp.url']) trace['lcp.url'] = imageHost(trace['lcp.url']);
  scrubSpanData(trace);
  if (Array.isArray(event.spans)) event.spans.forEach(scrubSpan);
  // Every breadcrumb passes beforeBreadcrumb when it's added; this catches
  // any that didn't. Scrubbing twice changes nothing.
  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs.forEach(scrubBreadcrumb);
  }
  for (const value of event.exception?.values ?? []) {
    if (typeof value.value === 'string') value.value = scrubUrlsIn(value.value);
  }
  return event;
}

// Session Replay shows what led to an error, but Bosko's screens are full of
// private writing: Journal entries, chats, prayer intentions, account details.
// So the browser masks every text node, input value and text-bearing attribute
// and blocks every image before a frame is recorded; a replay keeps only
// layout and interactions (#510; the other channels are in sentry-privacy.js).
// These are the SDK defaults, stated so an upgrade can't quietly loosen them.
// Don't add `unmask` or `unblock`: a subtree unmasked for harmless chrome
// today is where private text lands tomorrow.
export const REPLAY_PRIVACY = Object.freeze({
  maskAllText: true,
  maskAllInputs: true,
  blockAllMedia: true,
});

export function replayOptions() {
  return {
    ...REPLAY_PRIVACY,
    mask: [...REPLAY_MASK],
    maskAttributes: [...REPLAY_MASK_ATTRIBUTES],
    block: [...REPLAY_BLOCK],
    maskFn: maskReplayText,
    beforeAddRecordingEvent: scrubRecordingEvent,
  };
}

let initialized = false;

export function initSentry({ dsn, environment, release }) {
  if (!dsn || initialized) return;
  // Don't ship local development events to Sentry. Vite HMR and mid-edit
  // ReferenceErrors (e.g. BOSKO-FRONTEND-46) are dev noise, not real defects —
  // telemetry is only meaningful from staging/production.
  if (environment === 'development') return;
  initialized = true;

  const native = Capacitor.isNativePlatform();

  // Telemetry must never block app boot. initSentry runs synchronously in the
  // entrypoint before first render; on native, @sentry/capacitor's Sentry.init
  // drives the OS SDK across the Capacitor bridge, and if that throws (e.g. the
  // native plugin failed to link) it would otherwise take the whole app down —
  // the app would hang on the loading spinner forever. Contain any failure here.
  try {
    // @sentry/capacitor captures native iOS/Android crashes (Swift/Kotlin) and
    // routes JS errors through the OS SDK on device; on web it transparently
    // runs the React SDK. The second argument is the sibling (React) initializer.
    Sentry.init(
      {
        dsn,
        environment,
        release,
        // The reverse-proxy tunnel is a browser-transport concern; on native
        // the OS SDK ships events directly, so only set it on web.
        ...(native ? {} : { tunnel: '/sentry/tunnel' }),
        integrations: [],
        // Browsers that inject their own user scripts (Firefox/Brave on iOS,
        // the Instagram and Facebook in-app webviews) trip our global onerror
        // handler, so their failures arrive looking like ours. Drop them.
        beforeSend: (event) =>
          isInjectedScriptError(event) || isAnalyticsOfflineError(event)
            ? null
            : scrubEvent(event),
        beforeSendTransaction: scrubEvent,
        beforeSendSpan: scrubStandaloneSpan,
        beforeBreadcrumb: scrubBreadcrumb,
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        initialScope: {
          tags: { platform: Capacitor.getPlatform() },
        },
      },
      SentryReact.init,
    );

    // Browser tracing + session replay are web-only. Defer until the browser is
    // idle, and skip on native (WKWebView lacks requestIdleCallback and replay
    // is unsupported there). Native crash reporting is handled by the OS SDK.
    if (!native && typeof requestIdleCallback === 'function') {
      requestIdleCallback(
        () => {
          SentryReact.addIntegration(SentryReact.browserTracingIntegration());
          // Every full replay snapshot records location.href verbatim, and a
          // bearer link's token is in it, so a replay never starts there.
          if (hasBearerPath(window.location.pathname)) return;
          SentryReact.addEventProcessor(scrubReplayEvent);
          SentryReact.addIntegration(
            SentryReact.replayIntegration(replayOptions()),
          );
        },
        { timeout: 10000 },
      );
    }
  } catch (e) {
    console.error('[sentry] init failed; continuing without telemetry', e);
  }
}

export function setSentryUser(user) {
  try {
    if (!user) {
      Sentry.setUser(null);
      return;
    }
    Sentry.setUser({ id: user.id });
  } catch (e) {
    console.error('[sentry] setUser failed', e);
  }
}

export const SentryErrorBoundary = SentryReact.ErrorBoundary;
